import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PrioriteBadge, StatutBadge, STATUT_DOSSIER, PRIORITES } from "@/components/StatusBadge";
import { Plus, Search, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import DiscussionThread from "@/components/DiscussionThread";

const TYPES = ["courrier_entrant", "courrier_sortant", "email", "fax", "autre"];

export default function Dossiers() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [statutFilter, setStatutFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titre: "", type: "courrier_entrant", description: "", client_id: "", service: "", priorite: "normale", reference_externe: "",
  });

  const load = async () => {
    const params = { limit: 300 };
    if (statutFilter !== "all") params.statut = statutFilter;
    if (q) params.q = q;
    const { data } = await api.get("/dossiers", { params });
    setRows(data);
  };
  const loadClients = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };
  const loadUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (err) {
      console.warn("[Dossiers] loadUsers failed", err?.response?.status || err?.message);
    }
  };

  useEffect(() => { load(); }, [statutFilter]);
  useEffect(() => { loadClients(); loadUsers(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.client_id) delete payload.client_id;
      const { data } = await api.post("/dossiers", payload);
      toast.success(`Dossier créé (${data.numero})`);
      setOpen(false);
      setForm({ titre: "", type: "courrier_entrant", description: "", client_id: "", service: "", priorite: "normale", reference_externe: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const updateStatut = async (id, statut) => {
    try {
      await api.patch(`/dossiers/${id}`, { statut });
      toast.success("Statut mis à jour");
      load();
      if (detail?.id === id) setDetail({ ...detail, statut });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const updateAssign = async (id, assigne_a) => {
    try {
      await api.patch(`/dossiers/${id}`, { assigne_a });
      toast.success("Affectation mise à jour");
      load();
      if (detail?.id === id) setDetail({ ...detail, assigne_a });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const displayed = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      r.titre?.toLowerCase().includes(s) || r.numero?.toLowerCase().includes(s) || r.description?.toLowerCase().includes(s)
    );
  }, [rows, q]);

  return (
    <div>
      <PageHeader
        eyebrow="Module"
        title="Dossiers"
        description="Enregistrement, affectation, traitement, validation et clôture des dossiers."
        testid="dossiers-header"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="dossier-create-btn" className="bg-blue-900 hover:bg-blue-800 rounded-full">
                <Plus className="w-4 h-4 mr-2" /> Nouveau dossier
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">Créer un dossier</DialogTitle>
                <DialogDescription>Renseignez les informations principales.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submitCreate} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Titre</Label>
                  <Input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} data-testid="dossier-titre-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="dossier-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Priorité</Label>
                    <Select value={form.priorite} onValueChange={(v) => setForm({ ...form, priorite: v })}>
                      <SelectTrigger data-testid="dossier-priorite-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Client (optionnel)</Label>
                  <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="dossier-client-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nom} {c.prenom || ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Description</Label>
                  <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="dossier-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Service</Label>
                    <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Référence externe</Label>
                    <Input value={form.reference_externe} onChange={(e) => setForm({ ...form, reference_externe: e.target.value })} />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={saving} data-testid="dossier-submit-btn">Créer</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher un titre, numéro..."
                className="pl-9"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                data-testid="dossier-search-input"
              />
            </div>
            <Select value={statutFilter} onValueChange={setStatutFilter}>
              <SelectTrigger className="w-44" data-testid="dossier-statut-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {STATUT_DOSSIER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Titre</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead>Créé le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400"><FolderKanban className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucun dossier</TableCell></TableRow>
                ) : displayed.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid={`dossier-row-${r.numero}`}>
                    <TableCell className="font-mono text-xs text-slate-700">{r.numero}</TableCell>
                    <TableCell className="font-medium text-slate-900">{r.titre}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{r.type?.replace("_", " ")}</TableCell>
                    <TableCell><StatutBadge value={r.statut} /></TableCell>
                    <TableCell><PrioriteBadge value={r.priorite} /></TableCell>
                    <TableCell className="text-slate-500 text-xs">{new Date(r.created_at).toLocaleString("fr-FR")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  <span className="font-mono text-sm text-slate-500">{detail.numero}</span>
                  <span>{detail.titre}</span>
                </DialogTitle>
                <DialogDescription>Type {detail.type?.replace("_", " ")} · créé le {new Date(detail.created_at).toLocaleString("fr-FR")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Statut</Label>
                    <Select value={detail.statut} onValueChange={(v) => updateStatut(detail.id, v)}>
                      <SelectTrigger data-testid="dossier-detail-statut"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUT_DOSSIER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Affecté à</Label>
                    <Select value={detail.assigne_a || "none"} onValueChange={(v) => updateAssign(detail.id, v === "none" ? null : v)}>
                      <SelectTrigger data-testid="dossier-detail-assign"><SelectValue placeholder="Non affecté" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non affecté</SelectItem>
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Titre</Label>
                  <Input
                    defaultValue={detail.titre || ""}
                    onBlur={(e) => e.target.value !== (detail.titre || "") && api.patch(`/dossiers/${detail.id}`, { titre: e.target.value }).then(() => { toast.success("Titre mis à jour"); load(); setDetail({ ...detail, titre: e.target.value }); })}
                    data-testid="dossier-detail-titre"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Type</Label>
                    <Select value={detail.type} onValueChange={(v) => api.patch(`/dossiers/${detail.id}`, { type: v }).then(() => { toast.success("Type mis à jour"); load(); setDetail({ ...detail, type: v }); })}>
                      <SelectTrigger data-testid="dossier-detail-type"><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Priorité</Label>
                    <Select value={detail.priorite} onValueChange={(v) => api.patch(`/dossiers/${detail.id}`, { priorite: v }).then(() => { toast.success("Priorité mise à jour"); load(); setDetail({ ...detail, priorite: v }); })}>
                      <SelectTrigger data-testid="dossier-detail-prio"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Textarea
                    rows={3}
                    defaultValue={detail.description || ""}
                    onBlur={(e) => e.target.value !== (detail.description || "") && api.patch(`/dossiers/${detail.id}`, { description: e.target.value }).then(() => { toast.success("Description mise à jour"); load(); setDetail({ ...detail, description: e.target.value }); })}
                    data-testid="dossier-detail-description"
                    className="mt-1 bg-slate-50/60"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-xs text-slate-500">Service</Label>
                    <Input
                      defaultValue={detail.service || ""}
                      onBlur={(e) => e.target.value !== (detail.service || "") && api.patch(`/dossiers/${detail.id}`, { service: e.target.value }).then(() => { toast.success("Service mis à jour"); load(); setDetail({ ...detail, service: e.target.value }); })}
                      data-testid="dossier-detail-service"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Référence externe</Label>
                    <Input
                      defaultValue={detail.reference_externe || ""}
                      onBlur={(e) => e.target.value !== (detail.reference_externe || "") && api.patch(`/dossiers/${detail.id}`, { reference_externe: e.target.value }).then(() => { toast.success("Référence mise à jour"); load(); setDetail({ ...detail, reference_externe: e.target.value }); })}
                      data-testid="dossier-detail-refext"
                    />
                  </div>
                </div>
                <DiscussionThread entityType="dossier" entityId={detail.id} title="Discussion sur ce dossier" />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
